import React, { useEffect, useState } from 'react';
import { Phone } from 'lucide-react';
import { apiClient } from '@/lib/api';
import logger from '@/lib/logger';

/**
 * Hook: fetch the lead's last-pickup-per-number map once when the modal
 * opens.  Returns { winnerPhone, byPhone, totalPickups, loading }.
 *
 * `byPhone` is keyed by the ORIGINAL phone string stored on the lead
 * (primary / secondary / each additional_number) so callers can do a
 * simple equality check on the chip they're rendering.
 */
export function useLastPickup(leadId, enabled = true) {
    const [data, setData] = useState({
        winnerPhone: null,
        byPhone: {},
        totalPickups: 0,
        loading: false,
    });

    useEffect(() => {
        if (!leadId || !enabled) {
            setData({ winnerPhone: null, byPhone: {}, totalPickups: 0, loading: false });
            return;
        }
        let cancelled = false;
        setData((d) => ({ ...d, loading: true }));
        apiClient
            .get(`/leads/${leadId}/last-pickup`)
            .then((res) => {
                if (cancelled) return;
                const body = res?.data || {};
                setData({
                    winnerPhone: body.phone || null,
                    byPhone: body.by_phone || {},
                    totalPickups: body.total_pickups || 0,
                    loading: false,
                });
            })
            .catch((e) => {
                if (cancelled) return;
                logger.warn('last-pickup fetch failed', e);
                setData({ winnerPhone: null, byPhone: {}, totalPickups: 0, loading: false });
            });
        return () => { cancelled = true; };
    }, [leadId, enabled]);

    return data;
}

/**
 * Small green pill rendered next to whichever phone chip matched the
 * customer's last successful pickup.  Shows the agent name on hover
 * via title attr so reps know who connected.
 */
export default function LastPickupBadge({ pickup, isWinner = false, compact = false }) {
    if (!pickup) return null;
    const when = pickup.last_pickup_at ? new Date(pickup.last_pickup_at) : null;
    const fmt = (d) => {
        if (!d) return '';
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        if (days === 0) return 'today';
        if (days === 1) return 'yesterday';
        if (days < 30) return `${days}d ago`;
        if (days < 365) return `${Math.floor(days / 30)}mo ago`;
        return `${Math.floor(days / 365)}y ago`;
    };
    const title = pickup.agent_name
        ? `Picked up by ${pickup.agent_name} • ${pickup.call_duration}s • ${when?.toLocaleString() || ''}`
        : `Last pickup ${fmt(when)}`;
    return (
        <span
            title={title}
            className={`inline-flex items-center gap-1 rounded-full ${isWinner ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-emerald-50 text-emerald-600 border-emerald-200'} border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide`}
            data-testid="last-pickup-badge"
        >
            <span className={`h-1.5 w-1.5 rounded-full ${isWinner ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-400'}`} />
            {compact ? 'Pickup' : `Picked up ${fmt(when)}`}
            {!compact && pickup.count > 1 && (
                <span className="ml-0.5 text-emerald-500/80">×{pickup.count}</span>
            )}
        </span>
    );
}

/**
 * Convenience: returns the per-chip pickup entry for a given phone
 * string, doing a flexible last-9-digit match so format variants don't
 * miss.
 */
export function pickupForPhone(byPhone, phone) {
    if (!phone || !byPhone) return null;
    if (byPhone[phone]) return byPhone[phone];
    const clean = String(phone).replace(/\D/g, '');
    if (clean.length < 7) return null;
    const last9 = clean.slice(-9);
    for (const [k, v] of Object.entries(byPhone)) {
        const kc = String(k).replace(/\D/g, '');
        if (kc.length >= 7 && kc.slice(-9) === last9) return v;
    }
    return null;
}
